// Shared components
import { CountrySelectComponent } from '@components/shared/country-select/country-select.component';
import { TranslateModule } from '@ngx-translate/core';
// Models and services
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { GenderClientService } from '@services/api/gender/gender-client.service';
import { IGenderModel } from '@services/api/gender/gender-model.interface';
import { HardCodedConfigService } from '@services/util/hard-coded-config.service';
import { LogService } from '@services/util/log.service';
import { SelectItemLoaderService } from '@services/util/select-item-loader.service';
import { DatePickerModule } from 'primeng/datepicker';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
// PrimeNG imports
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BookingActionEvent } from '../../models/booking-detail.model';
import { BookingChangeTrackerService } from '../../services/booking-change-tracker.service';

/**
 * Interface for country data used in dropdowns
 */
interface CountryModel {
  code: string;
  name: string;
}

/**
 * Interface for simple gender selection
 */
interface Gender {
  id: number;
  iri: string;
  name: string;
}

@Component({
  selector: 'app-booking-personal-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    InputTextModule,
    InputNumberModule,
    InputMaskModule,
    RadioButtonModule,
    DatePickerModule,
    CountrySelectComponent
  ],
  templateUrl: './booking-personal-tab.component.html'
})
export class BookingPersonalTabComponent implements OnInit, OnChanges {
  @Input() booking: IBookingModel | null = null;
  @Output() actionEvent = new EventEmitter<BookingActionEvent>();

  loading = false;

  genders: Gender[] = [];

  // Store selected gender as a number (ID)
  selectedGenderId: number = 0;

  // Services
  private readonly selectItemLoader = inject(SelectItemLoaderService);
  private readonly genderService = inject(GenderClientService);
  private readonly logService = inject(LogService);
  readonly changeTracker = inject(BookingChangeTrackerService);
  private readonly config = inject(HardCodedConfigService);

  ngOnInit(): void {
    // Load genders first
    this.loadGenders();
    // We'll initialize in ngOnChanges when booking arrives
  }

  /**
   * Handle changes to the booking input property
   * This ensures we initialize gender selection whenever booking data changes/arrives
   */
  ngOnChanges(changes: SimpleChanges): void {
    // Check if booking changed and is non-null
    if (changes['booking'] && changes['booking'].currentValue) {
      this.initializeGuestData();
    }
  }

  /**
   * Load genders from the API
   */
  private loadGenders(): void {
    // Define hardcoded options as fallback
    this.genders = [
      {
        id: this.config.GENDER_MALE_ID,
        name: 'booking.detail.male',
        iri: `/api/genders/${this.config.GENDER_MALE_ID}`
      },
      {
        id: this.config.GENDER_FEMALE_ID,
        name: 'booking.detail.female',
        iri: `/api/genders/${this.config.GENDER_FEMALE_ID}`
      },
      {
        id: this.config.GENDER_OTHER_ID,
        name: 'booking.detail.other',
        iri: `/api/genders/${this.config.GENDER_OTHER_ID}`
      }
    ];

    // Load genders from API for complete data
    this.genderService.getItems().subscribe({
      next: (response) => {
        // We keep the same structure but get the real IRI values from the API
        if (response && response.items && response.items.length > 0) {
          // Map API gender data to our internal format
          const mappedGenders = response.items.map((g: IGenderModel) => ({
            id: g.id as number,
            name: this.getTranslationKeyForGender(g.id as number),
            iri: g['@id']
          }));

          this.genders = mappedGenders;

          // Now that we have genders loaded, initialize the guest data to ensure correct mapping
          this.initializeGuestData();
        } else {
          this.initializeGuestData();
        }
      },
      error: (err) => {
        this.logService.error('Error loading genders', err);
        // Even on error, try to initialize with fallback data
        this.initializeGuestData();
      }
    });
  }

  /**
   * Map gender IDs to translation keys
   */
  private getTranslationKeyForGender(id: number): string {
    switch (id) {
      case this.config.GENDER_MALE_ID:
        return 'booking.detail.male';
      case this.config.GENDER_FEMALE_ID:
        return 'booking.detail.female';
      case this.config.GENDER_OTHER_ID:
        return 'booking.detail.other';
      default:
        return 'booking.detail.other';
    }
  }

  /**
   * Initialize data from booking if available
   */
  private initializeGuestData(): void {
    if (!this.booking) {
      return;
    }

    // Set gender selection based on booking's gender property
    if (this.booking.gender) {
      // If we have a gender object, use its ID for the selection
      if (this.booking.gender.id) {
        const numericId = Number(this.booking.gender.id);
        this.selectedGenderId = numericId;

        // Check if the genders array includes this id
        this.genders.find((g) => g.id === numericId);
      }
      // Fallback to extracting ID from the IRI if ID is missing
      else if (this.booking.gender['@id']) {
        const genderId = this.booking.gender['@id'].split('/').pop();
        const numericId = Number(genderId) || this.config.GENDER_OTHER_ID;
        this.selectedGenderId = numericId;
      }
    } else {
      // Default to 'other' if no gender is specified
      this.selectedGenderId = this.config.GENDER_OTHER_ID;
    }
  }

  /**
   * Set loading state
   */
  private setLoading(isLoading: boolean): void {
    setTimeout(() => {
      this.loading = isLoading;
    });
  }

  /**
   * Handle personal information changes for a specific field
   * @param fieldName Name of the field that changed
   * @param newValue New value of the field
   */
  onFieldChange(fieldName: string, newValue: any): void {
    // Emit field change to parent component
    if (this.booking) {
      // Special handling for gender to ensure it's properly formatted for API
      if (fieldName === 'gender' && newValue && typeof newValue === 'object') {
        // For gender, we need to set the gender as a reference with @id
        // We're creating a minimal reference object with just the IRI
        // which is all that's needed for the API to create the relation
        const genderReference: Partial<IGenderModel> = { '@id': newValue.iri };
        this.booking.gender = genderReference as IGenderModel;
      } else {
        // Handle other fields using type-safe approach
        switch (fieldName) {
          case 'name':
            this.booking.name = newValue;
            break;
          case 'email_address':
            this.booking.email_address = newValue;
            break;
          case 'phone_number':
            this.booking.phone_number = newValue;
            break;
          case 'nationality':
            this.booking.nationality = newValue;
            break;
        }
      }

      this.actionEvent.emit({
        action: 'field-change',
        booking: this.booking,
        additionalData: {
          fieldName,
          newValue
        }
      });
    }
  }

  /**
   * Handle personal information changes
   * Legacy method kept for backward compatibility
   */
  onPersonalInfoChange(): void {
    // This is now handled by more granular field change tracking
    // No-op for backward compatibility
  }
}
